import { type FC, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Calendar, Heart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button } from '@/components/ui';
import { supabase } from '@/lib/api/supabase';

interface VolunteerSettings {
  volunteer_title: string;
  volunteer_description: string;
  volunteer_body: string;
  volunteer_card1_title: string;
  volunteer_card1_description: string;
  volunteer_card2_title: string;
  volunteer_card2_description: string;
  volunteer_card3_title: string;
  volunteer_card3_description: string;
  volunteer_questions_header: string;
  volunteer_questions_content: string;
  volunteer_questions_button_text: string;
  volunteer_questions_button_link: string;
}

/**
 * Volunteer information page
 * Displays volunteer opportunities and sign-up information
 * Content is editable from the admin panel via site_settings
 */
export const VolunteerPage: FC = () => {
  const [settings, setSettings] = useState<VolunteerSettings>({
    volunteer_title: 'Volunteer With Us',
    volunteer_description: 'Help make Cactus Cup an amazing experience for everyone!',
    volunteer_body: '',
    volunteer_card1_title: 'Join Our Team',
    volunteer_card1_description: 'Be part of an amazing team of volunteers who make Cactus Cup possible.',
    volunteer_card2_title: 'Flexible Shifts',
    volunteer_card2_description: 'Choose shifts that work with your schedule and still enjoy the event.',
    volunteer_card3_title: 'Perks & Benefits',
    volunteer_card3_description: 'Volunteers receive special perks as a thank you for their time.',
    volunteer_questions_header: 'Questions?',
    volunteer_questions_content: 'Have questions about volunteering? Check out our About page or reach out to the organizing team.',
    volunteer_questions_button_text: 'Contact Us',
    volunteer_questions_button_link: '/about',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true);

        // Fetch volunteer settings from site_settings
        const { data: settingsData } = await supabase
          .from('site_settings')
          .select('setting_key, setting_value')
          .in('setting_key', [
            'volunteer_title',
            'volunteer_description',
            'volunteer_body',
            'volunteer_card1_title',
            'volunteer_card1_description',
            'volunteer_card2_title',
            'volunteer_card2_description',
            'volunteer_card3_title',
            'volunteer_card3_description',
            'volunteer_questions_header',
            'volunteer_questions_content',
            'volunteer_questions_button_text',
            'volunteer_questions_button_link',
          ]);

        if (settingsData) {
          const settingsMap = settingsData.reduce((acc, item) => {
            acc[item.setting_key as keyof VolunteerSettings] = item.setting_value;
            return acc;
          }, {} as VolunteerSettings);

          setSettings(prev => ({
            ...prev,
            ...settingsMap,
          }));
        }
      } catch (err) {
        console.error('Failed to fetch volunteer page data:', err);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, []);

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
          <p className="mt-4 text-white">Loading...</p>
        </div>
      </div>
    );
  }

  // Parse body content - split by double newlines for paragraphs
  const bodyParagraphs = settings.volunteer_body.split('\n\n').filter(p => p.trim());

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[300px] flex items-center justify-center py-12">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-viking mb-4 drop-shadow-lg">
              {settings.volunteer_title}
            </h1>
            <p className="text-xl font-medium drop-shadow-md">
              {settings.volunteer_description}
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 max-w-5xl">
        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
            <CardHeader>
              <Users className="h-8 w-8 text-orange-500 mb-2" />
              <CardTitle className="text-lg">{settings.volunteer_card1_title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{settings.volunteer_card1_description}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
            <CardHeader>
              <Calendar className="h-8 w-8 text-orange-500 mb-2" />
              <CardTitle className="text-lg">{settings.volunteer_card2_title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{settings.volunteer_card2_description}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
            <CardHeader>
              <Heart className="h-8 w-8 text-orange-500 mb-2" />
              <CardTitle className="text-lg">{settings.volunteer_card3_title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{settings.volunteer_card3_description}</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Primary Content */}
          <div className="lg:col-span-2 space-y-8 order-2 lg:order-1">
            <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
              <CardContent className="pt-6">
                <div className="prose prose-sm max-w-none space-y-4">
                  {bodyParagraphs.map((paragraph, idx) => (
                    <p key={idx} className="leading-relaxed whitespace-pre-line">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 order-1 lg:order-2">
            <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
              <CardHeader>
                <CardTitle className="font-viking">{settings.volunteer_questions_header}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm">
                  {settings.volunteer_questions_content}
                </p>
                <Link to={settings.volunteer_questions_button_link}>
                  <Button variant="default" className="w-full">
                    {settings.volunteer_questions_button_text}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
